/**
 * useWorkspaceFeatures — per-workspace feature gates for the multi-workstream plan.
 *
 * Distinct from the existing `useFeatureFlags` hook in this directory,
 * which talks to the system-wide `certification_features` table (the
 * global kill-switch for whether a feature is shipping at all). This
 * hook is the per-workspace rollout knob: even if a feature is shipping
 * globally, each workspace must be explicitly enrolled.
 *
 * Two-tier model: feature available  =  global flag on  AND  workspace flag on.
 *
 * Storage: a `feature_flags` sub-object inside `workspaces.settings`
 * JSONB. Mirror of fly-worker/_shared/services/feature-flags.ts.
 *
 * Defaults to all-false on load / RLS denial. Refetches on workspace
 * switch. No realtime subscription — admin toggles propagate when the
 * user reloads or switches workspaces.
 *
 * See docs/plans/multi-workstream-plan.md F2 for the flag inventory.
 */

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface WorkspaceFeatures {
  evidence_layer_v2_enabled: boolean;
  proactive_feed_enabled: boolean;
  intelligence_chat_v2_enabled: boolean;
  federated_team_view_enabled: boolean;
  bigquery_connector_enabled: boolean;
  /**
   * v1.5 — Thoughts feed. When enabled, the Intelligence tab renders
   * the new Thoughts feed (proactive cards) instead of the chat-first
   * IntelligenceV2 / classic surface.
   */
  thoughts_feed_enabled: boolean;
  /**
   * Sovereign: when true, this workspace uses the strict grounded-only chat
   * (single-shot, cite-or-refuse — no tools). Default false → the workspace uses
   * the full N-Agent (tools, conversation, citations, evidence). Only a regulated
   * workspace that needs the hard-refuse guarantee should set this true.
   */
  strict_compliance_mode: boolean;
}

export const DEFAULT_WORKSPACE_FEATURES: WorkspaceFeatures = {
  evidence_layer_v2_enabled: false,
  proactive_feed_enabled: false,
  intelligence_chat_v2_enabled: false,
  federated_team_view_enabled: false,
  bigquery_connector_enabled: false,
  thoughts_feed_enabled: false,
  strict_compliance_mode: false,
};

export function useWorkspaceFeatures(workspaceId: string | null | undefined): {
  features: WorkspaceFeatures;
  loading: boolean;
} {
  const [features, setFeatures] = useState<WorkspaceFeatures>(DEFAULT_WORKSPACE_FEATURES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) {
      setFeatures(DEFAULT_WORKSPACE_FEATURES);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from("workspaces")
        .select("settings")
        .eq("id", workspaceId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        // Fail closed.
        setFeatures(DEFAULT_WORKSPACE_FEATURES);
        setLoading(false);
        return;
      }
      const settings = (data.settings ?? {}) as Record<string, unknown>;
      const raw = (settings.feature_flags ?? {}) as Record<string, unknown>;
      setFeatures({
        evidence_layer_v2_enabled:    raw.evidence_layer_v2_enabled    === true,
        proactive_feed_enabled:       raw.proactive_feed_enabled       === true,
        intelligence_chat_v2_enabled: raw.intelligence_chat_v2_enabled === true,
        federated_team_view_enabled:  raw.federated_team_view_enabled  === true,
        bigquery_connector_enabled:   raw.bigquery_connector_enabled   === true,
        // v2.0: Thoughts feed is the default surface — ON unless explicitly
        // false (MOHAP set false by migration 20260530000000).
        thoughts_feed_enabled:        raw.thoughts_feed_enabled        !== false,
        strict_compliance_mode:       raw.strict_compliance_mode       === true,
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  return { features, loading };
}
